import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Plus, Copy, Calendar, Trash2, GripVertical
} from 'lucide-react'
import { mockMentorStudents, getBatches, type TaskType } from '@/data/mentor-mock-data'
import { toast } from 'sonner'

type AssignmentMode = 'single' | 'multiple' | 'batch'
type Priority = 'low' | 'medium' | 'high'

interface TaskFormData {
    id: string
    subject: 'Physics' | 'Chemistry' | 'Biology'
    chapterTopic: string
    taskType: TaskType
    expectedDuration: number
    priority: Priority
}

export function DailyPlanBuilderView() {
    const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('single')
    const [selectedStudent, setSelectedStudent] = useState('')
    const [selectedStudents, setSelectedStudents] = useState<string[]>([])
    const [selectedBatch, setSelectedBatch] = useState('')
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
    const [tasks, setTasks] = useState<TaskFormData[]>([])
    const [, setShowPreview] = useState(false)

    const batches = getBatches()
    const allStudents = mockMentorStudents

    const handleAddTask = () => {
        const newTask: TaskFormData = {
            id: `task-${Date.now()}`,
            subject: 'Physics',
            chapterTopic: '',
            taskType: 'reading',
            expectedDuration: 1.5,
            priority: 'medium'
        }
        setTasks([...tasks, newTask])
    }

    const handleUpdateTask = (id: string, field: keyof TaskFormData, value: any) => {
        setTasks(tasks.map(task =>
            task.id === id ? { ...task, [field]: value } : task
        ))
    }

    const handleDeleteTask = (id: string) => {
        setTasks(tasks.filter(task => task.id !== id))
    }

    const handleCopyPreviousDay = () => {
        toast.success('Copied tasks from previous day')
        // Simulate copying tasks
        handleAddTask()
    }

    const handlePublish = () => {
        if (tasks.length === 0) {
            toast.error('Please add at least one task')
            return
        }

        let recipientCount = 0
        if (assignmentMode === 'single' && selectedStudent) recipientCount = 1
        if (assignmentMode === 'multiple') recipientCount = selectedStudents.length
        if (assignmentMode === 'batch' && selectedBatch) {
            recipientCount = allStudents.filter(s => s.batch === selectedBatch).length
        }

        if (recipientCount === 0) {
            toast.error('Please select at least one student')
            return
        }

        toast.success(`Plan published to ${recipientCount} student${recipientCount > 1 ? 's' : ''}!`)
        setTasks([])
    }

    const totalHours = tasks.reduce((sum, task) => sum + task.expectedDuration, 0)

    const toggleStudentSelection = (studentId: string) => {
        if (selectedStudents.includes(studentId)) {
            setSelectedStudents(selectedStudents.filter(id => id !== studentId))
        } else {
            setSelectedStudents([...selectedStudents, studentId])
        }
    }

    const handleSelectAllStudents = () => {
        if (selectedStudents.length === allStudents.length) {
            setSelectedStudents([])
        } else {
            setSelectedStudents(allStudents.map(s => s.id))
        }
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div>
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Daily Plan Builder</h2>
                <p className="text-sm text-theme-secondary mt-1">
                    Create and assign daily tasks to students
                </p>
            </div>

            {/* Assignment Mode Selection */}
            <GlassmorphicCard className="p-4">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-theme-primary mb-2">
                            Assign To:
                        </label>
                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={() => setAssignmentMode('single')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${assignmentMode === 'single'
                                    ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg shadow-blue-500/25'
                                    : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                            >
                                ⚪ Single Student
                            </button>
                            <button
                                onClick={() => setAssignmentMode('multiple')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${assignmentMode === 'multiple'
                                    ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg shadow-blue-500/25'
                                    : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                            >
                                ⚪ Multiple Students
                            </button>
                            <button
                                onClick={() => setAssignmentMode('batch')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${assignmentMode === 'batch'
                                    ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg shadow-blue-500/25'
                                    : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                            >
                                ⚪ Batch
                            </button>
                        </div>
                    </div>

                    {/* Single Student Mode */}
                    {assignmentMode === 'single' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">
                                    Select Student
                                </label>
                                <select
                                    value={selectedStudent}
                                    onChange={(e) => setSelectedStudent(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary transition-all outline-none"
                                >
                                    <option value="">Choose a student...</option>
                                    {allStudents.map(student => (
                                        <option key={student.id} value={student.id}>{student.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary transition-all outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Multiple Students Mode */}
                    {assignmentMode === 'multiple' && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-theme-secondary">
                                    Select Students ({selectedStudents.length} selected)
                                </label>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleSelectAllStudents}
                                        className="px-3 py-1.5 bg-primary-blue text-white text-xs font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                                    >
                                        {selectedStudents.length === allStudents.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary transition-all outline-none text-sm"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto custom-scrollbar p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                {allStudents.map(student => (
                                    <label
                                        key={student.id}
                                        className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedStudents.includes(student.id)}
                                            onChange={() => toggleStudentSelection(student.id)}
                                            className="rounded"
                                        />
                                        <span className="text-sm text-theme-primary">{student.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Batch Mode */}
                    {assignmentMode === 'batch' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">
                                    Select Batch
                                </label>
                                <select
                                    value={selectedBatch}
                                    onChange={(e) => setSelectedBatch(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary transition-all outline-none"
                                >
                                    <option value="">Choose a batch...</option>
                                    {batches.map(batch => (
                                        <option key={batch} value={batch}>
                                            {batch} ({allStudents.filter(s => s.batch === batch).length} students)
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary transition-all outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Quick Actions */}
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={handleCopyPreviousDay}
                            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-theme-primary text-sm font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                        >
                            <Copy className="h-4 w-4" />
                            Copy Previous Day
                        </button>
                    </div>
                </div>
            </GlassmorphicCard>

            {/* Task Builder */}
            <GlassmorphicCard className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-theme-primary">
                        Tasks for Selected Student(s)
                        {tasks.length > 0 && (
                            <span className="ml-2 text-sm text-theme-secondary">
                                ({tasks.length} tasks, {totalHours.toFixed(1)}h total)
                            </span>
                        )}
                    </h3>
                    <button
                        onClick={handleAddTask}
                        className="px-4 py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Add Task
                    </button>
                </div>

                {/* Tasks List */}
                <div className="space-y-3">
                    <AnimatePresence>
                        {tasks.map((task) => (
                            <motion.div
                                key={task.id}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
                            >
                                <div className="flex items-start gap-3">
                                    <GripVertical className="h-5 w-5 text-slate-400 mt-2 cursor-grab" />
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {/* Subject Icon + Chapter/Topic */}
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-theme-secondary mb-1">
                                                Chapter/Topic
                                            </label>
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <select
                                                    value={task.subject}
                                                    onChange={(e) => handleUpdateTask(task.id, 'subject', e.target.value)}
                                                    className="w-full sm:w-auto px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm outline-none focus:border-primary-blue"
                                                >
                                                    <option value="Physics">📘 Physics</option>
                                                    <option value="Chemistry">🧪 Chemistry</option>
                                                    <option value="Biology">🧬 Biology</option>
                                                </select>
                                                <input
                                                    type="text"
                                                    value={task.chapterTopic}
                                                    onChange={(e) => handleUpdateTask(task.id, 'chapterTopic', e.target.value)}
                                                    placeholder="e.g. Thermodynamics Ch. 4"
                                                    className="w-full sm:flex-1 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm outline-none focus:border-primary-blue min-w-0"
                                                />
                                            </div>
                                        </div>

                                        {/* Task Type */}
                                        <div>
                                            <label className="block text-xs font-medium text-theme-secondary mb-1">
                                                Task Type
                                            </label>
                                            <select
                                                value={task.taskType}
                                                onChange={(e) => handleUpdateTask(task.id, 'taskType', e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm outline-none focus:border-primary-blue"
                                            >
                                                <option value="reading">Reading</option>
                                                <option value="mcq">MCQ Practice</option>
                                                <option value="test">Test</option>
                                                <option value="revision">Revision</option>
                                            </select>
                                        </div>

                                        {/* Duration */}
                                        <div>
                                            <label className="block text-xs font-medium text-theme-secondary mb-1">
                                                Duration (hours)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.5"
                                                min="0.5"
                                                max="8"
                                                value={task.expectedDuration}
                                                onChange={(e) => handleUpdateTask(task.id, 'expectedDuration', parseFloat(e.target.value))}
                                                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm outline-none focus:border-primary-blue"
                                            />
                                        </div>

                                        {/* Priority */}
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-theme-secondary mb-1">
                                                Priority
                                            </label>
                                            <div className="flex flex-wrap gap-2">
                                                {(['low', 'medium', 'high'] as Priority[]).map(priority => (
                                                    <button
                                                        key={priority}
                                                        onClick={() => handleUpdateTask(task.id, 'priority', priority)}
                                                        className={`flex-1 sm:flex-none px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${task.priority === priority
                                                            ? priority === 'high'
                                                                ? 'bg-red-500 text-white'
                                                                : priority === 'medium'
                                                                    ? 'bg-amber-500 text-white'
                                                                    : 'bg-blue-500 text-white'
                                                            : 'bg-white dark:bg-slate-900 text-theme-secondary border border-slate-200 dark:border-slate-700'
                                                            }`}
                                                    >
                                                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Delete Button */}
                                    <button
                                        onClick={() => handleDeleteTask(task.id)}
                                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {tasks.length === 0 && (
                        <div className="text-center py-12">
                            <Calendar className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                            <p className="text-theme-secondary">No tasks added yet. Click "Add Task" to get started.</p>
                        </div>
                    )}
                </div>
            </GlassmorphicCard>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
                <button className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-theme-primary font-semibold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                    Save Draft
                </button>
                <button
                    onClick={() => setShowPreview(true)}
                    className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-theme-primary font-semibold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                    Preview
                </button>
                <button
                    onClick={handlePublish}
                    className="px-6 py-3 bg-gradient-to-r from-primary-blue to-blue-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-blue-500/25"
                >
                    Publish Plan
                </button>
            </div>
        </div>
    )
}
